import React, { useRef, useEffect, useState, useCallback } from "react";
import { Slider } from "antd";

const CascadeSlider = ({ range = 100, value = 0, onChange, inlineView=false }) => {
  const canvasRef = useRef(null);
  const [localValue, setLocalValue] = useState(value);
  const isDragging = useRef(false);
  const dpr = window.devicePixelRatio || 1;

    // If inlineView === true, render Ant Design Slider instead
  // Helper function to draw rounded rectangles
  const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    if (typeof radius === "undefined") {
      radius = 5;
    }
    if (typeof radius === "number") {
      radius = { tl: radius, tr: radius, br: radius, bl: radius };
    } else {
      const defaultRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
      for (let side in defaultRadius) {
        radius[side] = radius[side] || defaultRadius[side];
      }
    }
    ctx.beginPath();
    ctx.moveTo(x + radius.tl, y);
    ctx.lineTo(x + width - radius.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    ctx.lineTo(x + width, y + height - radius.br);
    ctx.quadraticCurveTo(
      x + width,
      y + height,
      x + width - radius.br,
      y + height
    );
    ctx.lineTo(x + radius.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    ctx.lineTo(x, y + radius.tl);
    ctx.quadraticCurveTo(x, y, x + radius.tl, y);
    ctx.closePath();
    ctx.fill();
  };

  // Draw the slider on the canvas
  const drawSlider = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const centerY = height / 2;
    const barWidth = width / range;

    // Clear the canvas
    ctx.clearRect(0, 0, width, height);

    // For text styling
    ctx.textBaseline = "top";
    ctx.font = `${24 * dpr}px Roboto, sans-serif`;

    // Draw each bar
    for (let i = 0; i <= range; i++) {
      // Decide the color for the bar
      let fillStyle;
      if (i < localValue) {
        // Active range but not the "current" bar
        fillStyle = "rgba(147, 197, 253, 0.6)"; // a softer blue
      } else if (i > localValue) {
        // Inactive
        fillStyle = "#E5E7EB"; // gray-200
      } else {
        // Exactly the selected bar
        fillStyle = "rgba(96, 165, 250, 1)"; // highlight blue-400
      }

      ctx.fillStyle = fillStyle;

      // Calculate bar height ( grow it more if i === localValue )
      const baseBarHeight = (i / range) * (height / 2 - 10);
      const barHeightMultiplier = i === localValue ? 1.2 : 1;
      const barHeight = baseBarHeight * barHeightMultiplier;

      // Keep bar as wide as possible but with a small gap
      const x = i * barWidth;
      const gap = 3;
      const barActualWidth = barWidth - gap;

      // For nice round corners
      const radius = Math.min(8 * dpr, barActualWidth / 2, barHeight / 2);

      // Upward bar
      drawRoundedRect(ctx, x, centerY - barHeight, barActualWidth, barHeight, {
        tl: radius,
        tr: radius,
        br: 0,
        bl: 0,
      });

      // Downward bar
      drawRoundedRect(ctx, x, centerY, barActualWidth, barHeight, {
        tl: 0,
        tr: 0,
        br: radius,
        bl: radius,
      });
    }

    // Draw value indicator area
    ctx.save();
    const boxX = 10 * dpr;
    const boxY = 10 * dpr;
    const boxWidth = 100 * dpr;
    const boxHeight = 42 * dpr;
    const borderRadius = 8 * dpr;

    // Draw a rounded rectangle for the background
    ctx.fillStyle = "#F0F9FF"; // Light blue
    drawRoundedRect(ctx, boxX, boxY, boxWidth, boxHeight, borderRadius);

    // Draw the value text
    ctx.fillStyle = "#1E3A8A"; // a dark-ish blue
    ctx.fillText(`${localValue || '0'}%`, 28 * dpr, 22 * dpr);
    ctx.restore();
  }, [range, localValue, dpr]);

  // Update slider on value change
  useEffect(() => {
    drawSlider();
  }, [drawSlider]);

  // Calculate value from mouse/touch position
  const calculateValue = useCallback(
    (clientX) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      return Math.min(range, Math.max(0, Math.floor((x / rect.width) * range)));
    },
    [range]
  );

  // Update local value during drag
  const handleInteraction = useCallback(
    (clientX) => {
      const newValue = calculateValue(clientX);
      if (newValue !== null && newValue !== localValue) {
        setLocalValue(newValue);
      }
    },
    [calculateValue, localValue]
  );

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e) => {
      isDragging.current = true;
      handleInteraction(e.clientX);
    },
    [handleInteraction]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging.current) {
        handleInteraction(e.clientX);
      }
    },
    [handleInteraction]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      onChange?.(localValue);
    }
  }, [onChange, localValue]);

  // Touch event handlers
  const handleTouchStart = useCallback(
    (e) => {
      isDragging.current = true;
      handleInteraction(e.touches[0].clientX);
    },
    [handleInteraction]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (isDragging.current) {
        handleInteraction(e.touches[0].clientX);
      }
    },
    [handleInteraction]
  );

  const handleTouchEnd = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      onChange?.(localValue);
    }
  }, [onChange, localValue]);

  // Global event listeners
  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Handle canvas resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const { width, height } = parent.getBoundingClientRect();
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      drawSlider();
    };

    resizeCanvas();

    // Re-run resize if parent container changes size
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [dpr, drawSlider]);

    if (inlineView) {
    return (
      <div className="relative w-full max-w-[600px] mx-auto">
        <Slider
          min={0}
          max={range}
          value={localValue}
          onChange={(val) => {
            setLocalValue(val);
          }}
          onAfterChange={(val) => {
            onChange?.(val);
          }}
        />
      </div>
    );
  }


  return (
    <div className="relative w-full h-48 max-w-[600px] mx-auto">
      {/* Optional decorative background bar or gradient can go here */}
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-pointer"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
    </div>
  );
};

export default CascadeSlider;
